import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  CloudDownload,
  CloudUpload,
  Delete,
  Language,
  Refresh,
  Save,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import {
  approveCaption,
  deleteCaption,
  downloadCaption,
  fetchSessionCaptions,
  regenerateCaption,
  saveCaption,
  translateSessionCaption,
  uploadCaption,
} from "../../../actions/captionsActions";
import { fetchOneSession } from "../../../actions/sessionActions";
import { parseVTT, serializeVTT } from "../../../utils/vttCodec";
import { formatTimeToMMSS } from "../../../utils/timeFormat";
import { STATUS_COLOR, SEVERITY_COLOR, prettySource } from "../../../constants/captionStyle";

/**
 * Pretty rendering for per-flag detail blobs. Falls back to a key:value
 * summary if no specific renderer matches — keeps the editor useful
 * even after we add new flag types without updating this map.
 */
function flagDetailText(flag) {
  const d = flag.detail ?? {};
  switch (flag.flag_type) {
    case "name_substituted":
      return d.original && d.replaced ? `Replaced "${d.original}" → "${d.replaced}"` : null;
    case "name_unmatched":
      return d.first_found && d.expected_last
        ? `Found "${d.first_found}" but no "${d.expected_last}" follow-up.`
        : null;
    case "low_confidence_segment":
      return d.avg_logprob != null ? `avg_logprob = ${Number(d.avg_logprob).toFixed(2)}` : null;
    case "high_no_speech_prob":
      return d.no_speech_prob != null ? `no_speech_prob = ${Number(d.no_speech_prob).toFixed(2)}` : null;
    case "large_pause_in_audio":
      return d.gap_sec != null ? `${Number(d.gap_sec).toFixed(1)}s gap between segments` : null;
    case "duration_mismatch":
      return d.delta_ratio != null && d.expected_sec != null && d.transcribed_sec != null
        ? `Expected ${Math.round(d.expected_sec)}s, transcribed ${Math.round(d.transcribed_sec)}s (${(d.delta_ratio * 100).toFixed(0)}% off)`
        : null;
    case "unexpected_language":
      return d.detected && d.expected ? `Detected "${d.detected}", expected "${d.expected}".` : null;
    case "brand_term_lowercase":
    case "vocabulary_miss":
    default:
      return Object.keys(d).length > 0
        ? Object.entries(d)
            .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
            .join(" · ")
        : null;
  }
}

const CaptionsEditorPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sessionId: sessionIdParam, language } = useParams();
  const sessionId = parseInt(sessionIdParam, 10);
  const [searchParams] = useSearchParams();
  // Deep-link support: /captions/edit/:id/:lang?source=assemblyai opens
  // the editor with that source pre-selected. The review queue links
  // include this query param so clicking Edit on a specific row lands
  // on that row's source instead of the default.
  const urlSource = searchParams.get("source");
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const cueListRef = useRef(null);

  const captionsBySession = useSelector(s => s.captions.bySession);
  const captions = captionsBySession[sessionId] ?? [];

  // Phase 6: a session+language can hold multiple provider rows
  // (Whisper + AssemblyAI). All rows matching the URL language are
  // candidates; `source` picks which one this editor is operating on.
  const candidates = useMemo(() => captions.filter(c => c.language_code === language), [captions, language]);

  // Seed the picker from the URL `?source=` deep link ONCE at mount
  // via useState's lazy initializer. The effect below only handles
  // "current pick is no longer a valid candidate" (e.g. the operator
  // deleted the row) — it does NOT re-check urlSource on every render.
  // That used to create a feedback loop where every toggle click was
  // immediately snapped back to the URL's source.
  const [source, setSource] = useState(() => urlSource || null);
  useEffect(() => {
    if (candidates.length === 0) {
      return;
    }
    // Current pick is still a valid candidate — leave it alone so the
    // operator's toggle clicks stick.
    if (source && candidates.some(c => c.source === source)) {
      return;
    }
    // No valid pick (initial mount with no urlSource, or the picked
    // source disappeared from the candidate list). Default to Whisper
    // when present, otherwise the first available row.
    const whisperRow = candidates.find(c => c.source === "whisper");
    setSource((whisperRow ?? candidates[0]).source);
  }, [candidates, source]);

  const caption = candidates.find(c => c.source === source) ?? null;

  const [session, setSession] = useState(null);
  const [cues, setCues] = useState([]);
  const [vttLoaded, setVttLoaded] = useState(false);
  const [vttLoadError, setVttLoadError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeCueId, setActiveCueId] = useState(null);

  // Fetch caption metadata + session detail in parallel on mount.
  useEffect(() => {
    dispatch(fetchSessionCaptions(sessionId));
    dispatch(fetchOneSession(sessionId))
      .then(res => setSession(res?.data?.objects?.[0] ?? null))
      .catch(() => {});
  }, [dispatch, sessionId]);

  // Re-fetch and re-parse the VTT whenever the caption row's S3 URL or
  // last-updated timestamp changes (e.g. after a successful save or
  // regenerate). The `cancelled` flag guards against an older download
  // settling after a newer one — without it, the operator can upload a
  // new VTT (which triggers an updated_at bump → new fetch) while the
  // previous fetch is still in flight, and whichever resolves last
  // wins. Returning a cleanup that flips the flag ensures only the
  // latest effect updates state.
  useEffect(() => {
    if (!caption?.s3_url) {
      setVttLoaded(true);
      setCues([]);
      // Phase 6: this effect now also runs on source-picker changes,
      // not just initial-mount + s3_url updates. If the operator was
      // editing one source (dirty=true) and switched to another that
      // hasn't generated yet, `dirty` would otherwise stay true with
      // empty cues — the Save button would enable and POST an empty
      // VTT to the new source. Clear dirty here so the picker switch
      // is a clean reset.
      setDirty(false);
      setVttLoadError(null);
      return undefined;
    }
    let cancelled = false;
    setVttLoaded(false);
    setVttLoadError(null);
    dispatch(downloadCaption(sessionId, language, source))
      .then(async response => {
        if (cancelled) return;
        const text =
          typeof response.data?.text === "function" ? await response.data.text() : String(response.data || "");
        if (cancelled) return;
        const { cues: parsed, error } = parseVTT(text);
        if (error) {
          setVttLoadError(error);
          setCues([]);
        } else {
          setCues(parsed);
        }
        setDirty(false);
        setVttLoaded(true);
      })
      .catch(err => {
        if (cancelled) return;
        setVttLoadError(err?.message || "Failed to load VTT");
        setVttLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, sessionId, language, source, caption?.s3_url, caption?.updated_at]);

  // While the audio is playing, mark which cue corresponds to the
  // current timestamp and auto-scroll it into view if it's offscreen.
  const onTimeUpdate = useCallback(() => {
    const t = audioRef.current?.currentTime ?? 0;
    const active = cues.find(c => t >= c.start && t < c.end);
    if (active?.id !== activeCueId) {
      setActiveCueId(active?.id ?? null);
      if (active && cueListRef.current) {
        const el = cueListRef.current.querySelector(`[data-cue-id="${active.id}"]`);
        if (el) {
          const parentRect = cueListRef.current.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const out = elRect.top < parentRect.top || elRect.bottom > parentRect.bottom;
          if (out) el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
    }
  }, [cues, activeCueId]);

  const seekToCue = useCallback(cue => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = cue.start;
    if (audioRef.current.paused) audioRef.current.play();
  }, []);

  const onCueTextChange = (id, newText) => {
    setCues(prev => prev.map(c => (c.id === id ? { ...c, text: newText } : c)));
    setDirty(true);
  };

  const onApprove = () => {
    setSubmitting(true);
    dispatch(approveCaption(sessionId, language, undefined, source)).finally(() => setSubmitting(false));
  };

  const onSave = () => {
    setSubmitting(true);
    const vtt = serializeVTT(cues);
    dispatch(saveCaption(sessionId, language, vtt, () => setDirty(false), source)).finally(() => setSubmitting(false));
  };

  // Regenerate ALWAYS dispatches AssemblyAI now — Kelly picked it as
  // the production winner. Existing Whisper rows from before the
  // switch are still viewable + approvable via the source picker,
  // but no path in the CMS triggers a NEW Whisper run. Operators who
  // want a fresh transcription on a session that only has a Whisper
  // row get an AssemblyAI track alongside it; they then approve the
  // AssemblyAI one and delete the Whisper one.
  const onRegenerate = async () => {
    const existingAssembly = candidates.find(c => c.source === "assemblyai");
    const onlyHasWhisper = source === "whisper" && !existingAssembly;
    // Confirm only when the click visibly changes the source the
    // operator was just looking at — i.e. they're on a Whisper row
    // and we're about to spawn an AssemblyAI row alongside. Plain
    // re-runs on an AssemblyAI row don't need a prompt.
    if (onlyHasWhisper) {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Generate an AssemblyAI track?",
        text: "This session only has a Whisper track. Queues an AssemblyAI run alongside; the Whisper row stays available until you delete it.",
        showCancelButton: true,
        confirmButtonText: "Generate",
      });
      if (!isConfirmed) return;
    }
    setSubmitting(true);
    dispatch(
      regenerateCaption(
        sessionId,
        language,
        () => {
          // The new AssemblyAI row may not exist yet — give the queue
          // worker a beat before refetching so it appears as a source
          // card immediately.
          setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
        },
        { provider: "assemblyai" }
      )
    ).finally(() => setSubmitting(false));
  };

  // Translation trigger: only sensible on an approved EN caption. Hidden
  // for other source languages and for un-approved EN. Confirms once,
  // dispatches the queue job, then refreshes the caption list so the
  // new ES row shows up in `processing` → `auto_pending_review`.
  const canTranslate =
    language === "en" && (caption?.status === "human_reviewed" || caption?.status === "human_edited");
  const targetExists = !!captions.find(c => c.language_code === "es");
  const onTranslateToSpanish = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: targetExists ? "Re-translate to Spanish?" : "Translate to Spanish?",
      text: targetExists
        ? "An existing Spanish track will be reset to processing and replaced when the job completes."
        : "Queues a translation job. The Spanish track will appear in auto_pending_review when the job finishes.",
      showCancelButton: true,
      confirmButtonText: targetExists ? "Re-translate" : "Translate",
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      translateSessionCaption(sessionId, "en", "es", () => {
        Swal.fire({ icon: "success", title: "Queued", timer: 900 });
        // The job is async — give the queue a moment then refresh.
        // The CMS will keep showing `processing` until the job completes.
        setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
      })
    ).finally(() => setSubmitting(false));
  };

  // Re-translate trigger for the OPPOSITE direction of canTranslate:
  // we're viewing a translated track (typically `es`, source=translated)
  // and want to re-run the translation from the approved English source.
  // Required for failed-translation rows because "Regenerate from audio"
  // is non-sensical on a row that has no audio relationship — translated
  // tracks come from the EN VTT, not from the session's MP3.
  const isTranslatedSource = source === "translated";
  const englishSourceForRetranslate = captions.find(
    c => c.language_code === "en" && (c.status === "human_reviewed" || c.status === "human_edited")
  );
  const canReTranslate = isTranslatedSource && !!englishSourceForRetranslate;
  const onReTranslate = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Re-translate from English?",
      text: "The current translated track will be reset to processing and replaced when the job completes.",
      showCancelButton: true,
      confirmButtonText: "Re-translate",
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      translateSessionCaption(sessionId, "en", language, () => {
        Swal.fire({ icon: "success", title: "Queued", timer: 900 });
        setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
      })
    ).finally(() => setSubmitting(false));
  };

  const onDownload = async () => {
    try {
      const response = await dispatch(downloadCaption(sessionId, language, source));
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      // Include the source in the filename so a comparison download
      // doesn't overwrite the prior provider's file on disk.
      const suffix = source && source !== "whisper" && source !== "manual" ? `.${source}` : "";
      a.download = `session-${sessionId}-${language}${suffix}.vtt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      // errorHandler in the thunk already showed a toast.
    }
  };

  const onUploadClick = () => fileInputRef.current?.click();
  const onUploadChosen = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubmitting(true);
    dispatch(uploadCaption(sessionId, language, file, undefined, source)).finally(() => {
      setSubmitting(false);
      e.target.value = "";
    });
  };

  const onDelete = () => {
    if (
      !window.confirm(
        `Delete the ${language.toUpperCase()} (${source}) caption track for session ${sessionId}? This cannot be undone.`
      )
    )
      return;
    setSubmitting(true);
    dispatch(
      deleteCaption(
        sessionId,
        language,
        () => {
          // If another source row still exists for this language, stay
          // on the page so the operator can compare. Otherwise go back
          // to the queue.
          const otherSourceStillThere = candidates.some(c => c.source !== source);
          if (!otherSourceStillThere) navigate("/captions/queue");
        },
        source
      )
    ).finally(() => setSubmitting(false));
  };

  const canApprove = caption?.status === "auto_pending_review";

  const totalSec = useMemo(
    () => (cues.length > 0 ? cues[cues.length - 1].end : (caption?.duration_sec ?? 0)),
    [cues, caption]
  );

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/captions/queue")}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1, ml: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {session?.session_name || `Session #${sessionId}`}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", opacity: 0.85 }}>
              Caption editor · {language.toUpperCase()}
              {source ? ` · ${prettySource(source)}` : ""}
              {session?.session_length_in_sec ? ` · ${Math.round(session.session_length_in_sec / 60)} min` : ""}
              {dirty ? " · unsaved changes" : ""}
            </Typography>
          </Box>
          {caption?.status && (
            <Chip label={caption.status} color={STATUS_COLOR[caption.status] || "default"} sx={{ ml: 1 }} />
          )}
        </Toolbar>
      </AppBar>

      {/* Audio bar — sticky under the title so it stays visible while the
          operator scrolls through cues. */}
      <Box
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 2,
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          py: 1.5,
        }}
      >
        {session?.session_main_url ? (
          <audio
            ref={audioRef}
            src={session.session_main_url}
            controls
            preload="metadata"
            onTimeUpdate={onTimeUpdate}
            style={{ width: "100%" }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Loading session audio…
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2, py: 2, px: 1 }}>
        {/* Left: cue list editor */}
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="subtitle1">
                  <strong>Captions</strong> · {cues.length} cues · {Math.round(totalSec)}s
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click a timestamp to seek the audio. The cue playing now is highlighted.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button startIcon={<CloudDownload />} onClick={onDownload} size="small" variant="outlined">
                  Download .vtt
                </Button>
                <input
                  type="file"
                  accept=".vtt,text/vtt"
                  ref={fileInputRef}
                  onChange={onUploadChosen}
                  style={{ display: "none" }}
                />
                <Button startIcon={<CloudUpload />} onClick={onUploadClick} size="small" variant="outlined">
                  Upload edited .vtt
                </Button>
              </Stack>
            </Stack>

            {!caption ? (
              <Typography color="text.secondary">Loading caption metadata…</Typography>
            ) : !caption.s3_url ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No VTT file is stored yet — click{" "}
                <strong>{isTranslatedSource ? "Re-translate from English" : "Regenerate from audio"}</strong> on the
                right.
              </Typography>
            ) : !vttLoaded ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Fetching VTT…
                </Typography>
              </Stack>
            ) : vttLoadError ? (
              <Typography color="error.main" variant="body2">
                {vttLoadError}
              </Typography>
            ) : (
              <Box
                ref={cueListRef}
                sx={{
                  maxHeight: "calc(100vh - 360px)",
                  overflowY: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {cues.map(cue => {
                  const isActive = cue.id === activeCueId;
                  return (
                    <Box
                      key={cue.id}
                      data-cue-id={cue.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                        backgroundColor: isActive ? "action.selected" : "transparent",
                        transition: "background-color 120ms ease-in-out",
                        "&:hover": { backgroundColor: isActive ? "action.selected" : "action.hover" },
                      }}
                    >
                      <Tooltip title={`Seek to ${formatTimeToMMSS(cue.start)}`} placement="left">
                        <Button
                          size="small"
                          onClick={() => seekToCue(cue)}
                          sx={{
                            minWidth: 56,
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: isActive ? "primary.main" : "text.secondary",
                            mt: 0.5,
                          }}
                        >
                          {formatTimeToMMSS(cue.start)}
                        </Button>
                      </Tooltip>
                      <TextField
                        value={cue.text}
                        onChange={e => onCueTextChange(cue.id, e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                        InputProps={{ disableUnderline: true, sx: { fontSize: 14 } }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right: actions + flags */}
        <Box sx={{ width: 340, flexShrink: 0 }}>
          <Stack spacing={2}>
            {/* Phase 6 source picker — only renders when >1 provider's
                row exists for this language. Vertical orientation so
                the provider name + status chip get full row width in
                the 340px panel; the previous horizontal layout
                wrapped "Whisper-1" across two lines. */}
            {candidates.length > 1 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Caption source
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Multiple providers have a track for this language. Pick one to edit, approve, or compare.
                </Typography>
                <ToggleButtonGroup
                  value={source}
                  exclusive
                  onChange={(_e, next) => next && setSource(next)}
                  orientation="vertical"
                  size="small"
                  fullWidth
                >
                  {candidates.map(c => (
                    <ToggleButton
                      key={c.source}
                      value={c.source}
                      sx={{
                        textTransform: "none",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {prettySource(c.source)}
                      </Typography>
                      <Chip
                        label={(c.status || "").replace(/_/g, " ")}
                        size="small"
                        color={STATUS_COLOR[c.status] || "default"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Paper>
            )}

            {caption?.last_error && caption?.status === "failed" && (
              <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "error.main" }}>
                <Typography variant="subtitle2" color="error.main" gutterBottom>
                  Last error
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                  {caption.last_error}
                </Typography>
              </Paper>
            )}

            <Paper sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Tooltip title={canApprove ? "" : "Only auto_pending_review captions can be approved as-is."}>
                  <span>
                    <Button
                      fullWidth
                      startIcon={<CheckCircle />}
                      variant="contained"
                      color="success"
                      disabled={!canApprove || submitting || dirty}
                      onClick={onApprove}
                    >
                      Approve as-is
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  fullWidth
                  startIcon={<Save />}
                  variant="contained"
                  disabled={!vttLoaded || !dirty || submitting}
                  onClick={onSave}
                >
                  {dirty ? "Save edits" : "Saved"}
                </Button>
                {isTranslatedSource ? (
                  // Translated rows have no audio relationship — re-run
                  // the translation from the approved English source
                  // instead of dispatching an audio-transcription job
                  // that would create an unrelated whisper/assemblyai
                  // track alongside the broken translated one.
                  <Tooltip
                    title={
                      canReTranslate
                        ? ""
                        : "Need an approved English track on this session to re-translate from. Approve the EN row first."
                    }
                  >
                    <span>
                      <Button
                        fullWidth
                        startIcon={<Language />}
                        variant="outlined"
                        disabled={!canReTranslate || submitting}
                        onClick={onReTranslate}
                      >
                        Re-translate from English
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    fullWidth
                    startIcon={<Refresh />}
                    variant="outlined"
                    disabled={submitting}
                    onClick={onRegenerate}
                  >
                    Regenerate from audio
                  </Button>
                )}
                {canTranslate && (
                  <Button
                    fullWidth
                    startIcon={<Language />}
                    variant="outlined"
                    disabled={submitting}
                    onClick={onTranslateToSpanish}
                  >
                    {targetExists ? "Re-translate to Spanish" : "Translate to Spanish"}
                  </Button>
                )}
                <Divider />
                <Button
                  fullWidth
                  startIcon={<Delete />}
                  color="error"
                  variant="text"
                  disabled={submitting}
                  onClick={onDelete}
                >
                  Delete track
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Reviewer flags</strong>
              </Typography>
              {(caption?.flags ?? []).length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No flags raised by the post-processor.
                </Typography>
              ) : (
                <Stack spacing={1.5} divider={<Divider flexItem />}>
                  {caption.flags.map(flag => {
                    const detail = flagDetailText(flag);
                    return (
                      <Box key={flag.id}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={flag.flag_type.replace(/_/g, " ")}
                            color={SEVERITY_COLOR[flag.severity] || "default"}
                            size="small"
                            variant="outlined"
                          />
                          {flag.location_start_sec != null && (
                            <Button
                              size="small"
                              onClick={() => seekToCue({ start: flag.location_start_sec })}
                              sx={{ fontFamily: "monospace", fontSize: 12, minWidth: 0, px: 0.5 }}
                            >
                              {formatTimeToMMSS(flag.location_start_sec)}
                            </Button>
                          )}
                        </Stack>
                        {detail && (
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            {detail}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>
      </Box>
    </MainContentContainer>
  );
};

export default CaptionsEditorPage;
